import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Category, Discipline } from '../../types/domain';

export type BoardSlot =
  | {
      kind: 'DISCIPLINE';
      key: string;
      category: Category;
      discipline: Discipline;
      results: Result[];
    }
  | {
      kind: 'OVERALL';
      key: string;
      category: Category;
      ranking: OverallRanking;
    };

/**
 * Sestavi pole slotu pro rotujici board layouty (FOCUS, CAROUSEL, SPLIT).
 * Disciplines po kategoriich + volitelne overall karty po kazde kategorii.
 *
 * Prazdne discipliny (zadny vysledek) se z rotace automaticky vynechavaji —
 * plat by jinak pul casu ukazoval „zatim zadne vysledky". Pokud vsechny
 * discipliny jsou prazdne, vrati se `allSlots` fallback, aby nerotoval
 * nic a board mohl zobrazit placeholder.
 */
export const buildBoardSlots = (
  competition: CompetitionPublic,
  results: Result[],
  overall: OverallRanking[],
): BoardSlot[] => {
  const allSlots: BoardSlot[] = [];
  const includeOverall = competition.overallRankingVisible && competition.publicRotateOverall;
  for (const cat of competition.categories) {
    for (const disc of cat.disciplines) {
      allSlots.push({
        kind: 'DISCIPLINE',
        key: `d-${cat.id}-${disc.id}`,
        category: cat,
        discipline: disc,
        results: results.filter((r) => r.disciplineId === disc.id),
      });
    }
    if (includeOverall) {
      const ranking = overall.find((o) => o.categoryId === cat.id);
      if (ranking && ranking.teams.length > 0) {
        allSlots.push({
          kind: 'OVERALL',
          key: `o-${cat.id}`,
          category: cat,
          ranking,
        });
      }
    }
  }

  // Kdyz je zapnuto boardShowAllTeams, zapisovatel vidi i prazdne discipliny
  // s placeholdery "—" pro kazde druzstvo — rotace je proto nechava v sobe.
  // Jinak prazdne discipliny skrýváme, aby plat nerotoval pres 4× "zatim zadne vysledky".
  const includeEmpty = !!competition.boardShowAllTeams;
  const nonEmpty = allSlots.filter(
    (s) =>
      s.kind === 'OVERALL' ||
      (s.kind === 'DISCIPLINE' && (includeEmpty || s.results.length > 0)),
  );
  return nonEmpty.length > 0 ? nonEmpty : allSlots;
};
